import { ArrowLeft, Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useMembers } from '@/features/members';
import { useSession } from '@/stores/session';
import { TaskFormDialog } from '../components/TaskFormDialog';
import {
  PRIORITY_LABEL,
  TASK_STATUSES,
  canManageTasks,
  type TaskStatus,
} from '../constants';
import {
  useDeleteTask,
  useSetTaskStatus,
  useTask,
  useUpdateTask,
} from '../queries';
import type { TaskInput } from '../types';

const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

const PRIORITY_VARIANT: Record<string, 'destructive' | 'secondary' | 'outline'> = {
  high: 'destructive',
  medium: 'secondary',
  low: 'outline',
};

export function TaskDetailPage() {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();
  const { data: task, isPending, isError } = useTask(publicId);

  const canManage = canManageTasks(useSession((s) => s.role));
  const setStatus = useSetTaskStatus();
  const update = useUpdateTask();
  const remove = useDeleteTask();

  const [editOpen, setEditOpen] = useState(false);

  const { data: members } = useMembers();

  const back = () => navigate('/tasks');

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError || !task) {
    return (
      <div className="max-w-3xl space-y-4">
        <Button variant="ghost" size="sm" className="-ml-2" onClick={back}>
          <ArrowLeft /> Tasks
        </Button>
        <p className="text-muted-foreground">This task isn’t available.</p>
      </div>
    );
  }

  const assigneeName = task.assignee
    ? (members?.results.find((m) => m.user.id === task.assignee)?.user.full_name ?? '…')
    : '—';

  async function handleEdit(input: TaskInput) {
    await update.mutateAsync({ id: task!.public_id, input });
  }

  return (
    <div className="max-w-3xl space-y-6">
      <Button variant="ghost" size="sm" className="text-muted-foreground -ml-2" onClick={back}>
        <ArrowLeft /> Tasks
      </Button>

      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">{task.title}</h1>
        <Badge variant={PRIORITY_VARIANT[task.priority]}>{PRIORITY_LABEL[task.priority]}</Badge>
        {canManage ? (
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil /> Edit
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="icon" aria-label="Delete task">
                  <Trash2 />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this task?</AlertDialogTitle>
                  <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    disabled={remove.isPending}
                    onClick={async () => {
                      await remove.mutateAsync(task.public_id);
                      back();
                    }}
                  >
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 text-sm sm:grid-cols-3">
          <Field label="Status">
            <Select
              value={task.status}
              onValueChange={(v) => setStatus.mutate({ id: task.public_id, status: v as TaskStatus })}
            >
              <SelectTrigger className="h-8 w-40" aria-label="Task status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TASK_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Assignee">{assigneeName}</Field>
          <Field label="Due">{fmtDate(task.due_date)}</Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Description</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-foreground text-sm whitespace-pre-line">{task.description || '—'}</p>
        </CardContent>
      </Card>

      {canManage ? (
        <TaskFormDialog open={editOpen} onOpenChange={setEditOpen} task={task} onSubmit={handleEdit} />
      ) : null}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-muted-foreground text-xs font-medium uppercase">{label}</p>
      <div className="text-foreground mt-1">{children}</div>
    </div>
  );
}
